import { useRef, useEffect, useState, useCallback } from 'react'

interface UseChatScrollOptions {
  /** Whether currently streaming */
  isStreaming?: boolean
  /** Current streamed text */
  currentStreamedText?: string
  /** Messages array for dependency tracking */
  messages?: any[]
  /** Whether in chat mode (affects scroll listener attachment) */
  inChatMode?: boolean
}

interface UseChatScrollReturn {
  /** Ref for the messages container */
  messagesContainerRef: React.RefObject<HTMLDivElement>
  /** Ref for messages end marker */
  messagesEndRef: React.RefObject<HTMLDivElement>
  /** Whether to show scroll-to-bottom button */
  showScrollToBottom: boolean
  /** Scroll to bottom of messages */
  scrollToBottom: (force?: boolean) => void
  /** Scroll so user message appears at top (Claude/ChatGPT style) */
  scrollToUserMessage: () => void
  /** Force scroll to user message on next render */
  scheduleScrollToUserMessage: () => void
}

const useChatScroll = ({
  isStreaming = false,
  currentStreamedText = '',
  messages = [],
  inChatMode = false
}: UseChatScrollOptions = {}): UseChatScrollReturn => {
  // Refs
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  
  // Internal refs for scroll management
  const shouldAutoScrollRef = useRef(true)
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const pendingUserScrollRef = useRef(false)
  const userScrollTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  
  // State
  const [showScrollToBottom, setShowScrollToBottom] = useState(false)

  // Constants
  const SCROLL_THRESHOLD = 100 // pixels from bottom for consistency

  // Check if user is near bottom of scroll
  const isNearBottom = useCallback(() => {
    if (!messagesContainerRef.current) return true

    const container = messagesContainerRef.current
    const isNear = container.scrollTop + container.clientHeight >= container.scrollHeight - SCROLL_THRESHOLD

    return isNear
  }, [])

  // Check if assistant content exceeds viewport (for scroll-to-bottom button)
  const checkAssistantContentHeight = useCallback(() => {
    if (!messagesContainerRef.current) return false

    const container = messagesContainerRef.current
    const isAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - SCROLL_THRESHOLD
    
    return !isAtBottom
  }, [])

  // Scroll to bottom of messages
  const scrollToBottom = useCallback((force = false) => {
    if (!messagesContainerRef.current) return

    // Only auto-scroll if user is near bottom or if forced
    if (!force && !shouldAutoScrollRef.current) return

    const container = messagesContainerRef.current
    const targetScrollTop = container.scrollHeight - container.clientHeight

    container.scrollTo({
      top: targetScrollTop,
      behavior: 'smooth'
    })
  }, [])

  // Scroll so user message appears at top (Claude/ChatGPT style)
  const scrollToUserMessage = useCallback(() => {
    if (!messagesContainerRef.current) return

    const container = messagesContainerRef.current
    const userMessages = container.querySelectorAll('.userMessage')
    
    if (userMessages.length > 0) {
      // Get the last user message (most recent)
      const lastUserMessage = userMessages[userMessages.length - 1] as HTMLElement
      
      // Calculate scroll position to put user message at top
      const containerRect = container.getBoundingClientRect()
      const messageRect = lastUserMessage.getBoundingClientRect()
      const scrollTop = container.scrollTop + (messageRect.top - containerRect.top) - 24 // 24px padding
      
      container.scrollTo({
        top: scrollTop,
        behavior: 'smooth'
      })
    }
  }, [])

  // Schedule scroll to user message on next render
  const scheduleScrollToUserMessage = useCallback(() => {
    // Clear any existing user scroll timeout
    if (userScrollTimeoutRef.current) {
      clearTimeout(userScrollTimeoutRef.current)
    }
    
    pendingUserScrollRef.current = true
  }, [])

  // Throttled scroll during streaming
  const throttledScrollToBottom = useCallback(() => {
    // Don't auto-scroll during streaming if user scroll is pending
    if (pendingUserScrollRef.current) return
    
    // Clear any pending scroll
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current)
    }

    // Update auto-scroll preference based on user position
    shouldAutoScrollRef.current = isNearBottom()

    // Throttle scroll calls during streaming
    scrollTimeoutRef.current = setTimeout(() => {
      if (shouldAutoScrollRef.current && !pendingUserScrollRef.current) {
        scrollToBottom()
      }
    }, 100) // Throttle to 100ms
  }, [isNearBottom, scrollToBottom])

  // Handle scroll events
  const handleScrollEvent = useCallback(() => {
    if (!messagesContainerRef.current) return

    const shouldShowButton = checkAssistantContentHeight()
    setShowScrollToBottom(shouldShowButton)
    
    // Update auto-scroll preference
    shouldAutoScrollRef.current = isNearBottom()
  }, [checkAssistantContentHeight, isNearBottom])

  // Effect: Handle scroll behavior based on streaming and messages
  useEffect(() => {
    // Handle pending user scroll (highest priority)
    if (pendingUserScrollRef.current) {
      userScrollTimeoutRef.current = setTimeout(() => {
        scrollToUserMessage()
        pendingUserScrollRef.current = false
      }, 100) // Small delay to ensure DOM has updated
      return
    }

    if (isStreaming && currentStreamedText) {
      // During streaming, use throttled scroll
      throttledScrollToBottom()
    } else if (messages.length > 0) {
      // For new messages, scroll immediately but check position first
      if (!messagesContainerRef.current) return
      
      const container = messagesContainerRef.current
      const nearBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - SCROLL_THRESHOLD
      
      shouldAutoScrollRef.current = nearBottom
      if (nearBottom) {
        scrollToBottom(true) // Force scroll for new messages
      }
    }
  }, [messages.length, currentStreamedText, isStreaming]) // Reduced dependencies

  // Effect: Add scroll listener
  useEffect(() => {
    const container = messagesContainerRef.current
    if (!container) return

    container.addEventListener('scroll', handleScrollEvent, { passive: true })

    return () => {
      container.removeEventListener('scroll', handleScrollEvent)
      // Clean up all timeouts
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
      if (userScrollTimeoutRef.current) {
        clearTimeout(userScrollTimeoutRef.current)
      }
    }
  }, [inChatMode, handleScrollEvent]) // Re-attach when entering/leaving chat mode

  // Cleanup effect on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
      if (userScrollTimeoutRef.current) {
        clearTimeout(userScrollTimeoutRef.current)
      }
    }
  }, [])

  return {
    messagesContainerRef,
    messagesEndRef,
    showScrollToBottom,
    scrollToBottom,
    scrollToUserMessage,
    scheduleScrollToUserMessage
  }
}

export default useChatScroll 